import { Checkbox } from "components/ui/checkbox";
import { Field, FieldError, FieldLabel, FieldSet } from "components/ui/field";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import * as QueryKeys from "data";
import { useEffect, useState } from "react";

import { useIdentity } from "contexts/identity-context";
import * as Routes from "routes";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert, AlertDescription } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import { Spinner } from "components/ui/spinner";
import ConfirmationModal from "components/modals/confirmation-modal";
import BackButton from "components/navigation/back-button";
import { PermissionsKeys } from "components/permissions/permission-keys";
import {
  PermissionTooltipButton,
  TooltipWrapper,
} from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ApiKey, ApiProduct, ModifyApiKey, Status } from "data/models";
import { deleteApiKey, editApiKey } from "data/mutations-common";
import { fetchApiKeys, fetchApiProducts } from "data/queries-common";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Form, Formik } from "formik";
import { Ban, CheckCircle, CircleAlert, Copy, Gem, Info } from "lucide-react";
import { useHistory } from "react-router-dom";
import { resolveIdentityId } from "utils/identity";
import { updateApiKeyValidationSchema } from "components/api-keys/validation";

const keyStatus = [
  {
    value: "approved",
    label: "Approved",
    icon: <CheckCircle className="text-success" />,
  },
  {
    value: "revoked",
    label: "Revoked",
    icon: <Ban className="text-warning" />,
  },
];

export const EditApiKey = (props: any) => {
  const history = useHistory();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  const apiKeysQuery = useQuery({
    queryKey: [QueryKeys.apiKeys, identityId],
    queryFn: () => fetchApiKeys(),
  });

  useEffect(() => {
    if (apiKeysQuery.isSuccess && apiKeysQuery.data) {
      const selected = apiKeysQuery.data.filter(
        (key: ApiKey) => key.name === props.match.params.keyName,
      )[0];
      setSelectedKey(selected);
    }
  }, [apiKeysQuery.isSuccess]);

  const getSelectedApp = (apps: ApiKey[]) => {
    if (selectedKey !== null) return selectedKey;
    return apps.filter(
      (key: ApiKey) => key.name === props.match.params.keyName,
    )[0];
  };

  const apiProductsQuery = useQuery({
    queryKey: [QueryKeys.apiProducts],
    queryFn: () => fetchApiProducts(),
  });

  const queryClient = useQueryClient();
  const apiKeyUpdateMutation = useMutation({
    mutationFn: (values: ModifyApiKey) => {
      const newStatus: Status =
        values.status === "approved" ? "approve" : "revoke";
      return editApiKey({ ...values, status: newStatus });
    },
    onSuccess: (updatedApiKey) => {
      const updatedApiKeys = apiKeysQuery.data?.map((apiKey: ApiKey) => {
        if (apiKey.name === updatedApiKey.name) {
          return updatedApiKey;
        }

        return apiKey;
      });
      queryClient.setQueryData([QueryKeys.apiKeys, identityId], updatedApiKeys);
      setSelectedKey(updatedApiKey);
      setIsEditing(false);
    },
  });

  const apiKeyDeleteMutation = useMutation({
    mutationFn: () => deleteApiKey(selectedKey?.name ?? ""),
    onSuccess: (deletedApiKey) => {
      const remainingApiKeys = apiKeysQuery.data?.filter(
        (apiKey: ApiKey) => apiKey.name !== deletedApiKey.name,
      );

      queryClient.setQueryData(
        [QueryKeys.apiKeys, identityId],
        remainingApiKeys,
      );

      history.push(Routes.developerApiKeys);
    },
  });
  const premiumPermissionKeys = [PermissionsKeys.premiumDataRead];
  const isDisabledPremiumData = (isPremium?: boolean) => {
    if (isPremium === undefined) {
      return false;
    } else {
      return isPremium && !isPermitted(premiumPermissionKeys);
    }
  };

  const isLoading: boolean =
    apiKeysQuery.isPending || apiProductsQuery.isPending;
  const isError: boolean =
    apiKeysQuery.isError ||
    apiProductsQuery.isError ||
    apiKeyUpdateMutation.isError;

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  // Extend Day.js with plugins
  dayjs.extend(utc);
  dayjs.extend(timezone);

  return (
    <div className="container mx-auto space-y-4">
      <div className="flex justify-end">
        <BackButton />
      </div>
      <Card className="max-w-3xl mx-auto justify-center ">
        {isError && (
          <CardContent>
            <Alert variant="destructive">
              <CircleAlert />
              <AlertDescription>
                An error occurred while fetching the apps.
              </AlertDescription>
            </Alert>
          </CardContent>
        )}
        {isLoading && (
          <CardContent>
            <Skeleton className="m-4 h-64 w-full" />
          </CardContent>
        )}
        {apiKeysQuery.isSuccess && apiProductsQuery.isSuccess && (
          <Formik<ModifyApiKey>
            enableReinitialize={true}
            initialValues={{
              name: getSelectedApp(apiKeysQuery.data).name,
              status: getSelectedApp(apiKeysQuery.data).status,
              products: getSelectedApp(apiKeysQuery.data).products.map(
                (x) => x.productName,
              ),
              notes: getSelectedApp(apiKeysQuery.data).notes,
              displayName: getSelectedApp(apiKeysQuery.data).displayName,
            }}
            onSubmit={async (values) => {
              apiKeyUpdateMutation.mutate(values);
            }}
            validationSchema={updateApiKeyValidationSchema}
          >
            {({
              values: { displayName, status, products, notes },
              handleChange,
              handleBlur,
              isValid,
              dirty,
              resetForm,
              setFieldValue,
              errors,
              touched,
            }) => {
              return (
                <Form>
                  <CardContent>
                    <FieldSet>
                      <Field>
                        <FieldLabel htmlFor="displayName">Name</FieldLabel>
                        <Input
                          id="displayName"
                          name="displayName"
                          type="text"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={displayName}
                          maxLength={60}
                          disabled={!isEditing}
                          aria-invalid={
                            touched.displayName && Boolean(errors.displayName)
                          }
                        />
                        {touched.displayName && errors.displayName && (
                          <FieldError>{errors.displayName}</FieldError>
                        )}
                      </Field>
                      <Field>
                        <FieldLabel>App Status</FieldLabel>
                        <Select
                          value={status}
                          onValueChange={(value) =>
                            setFieldValue("status", value)
                          }
                          disabled={
                            !isEditing || apiKeyUpdateMutation.isPending
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {keyStatus.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                <div className="flex items-center gap-2.5">
                                  {option.icon} {option.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <div className="ml-3.5 flex h-10 flex-col">
                        <label
                          htmlFor="view-edit-value-consumer-key"
                          className="text-xs text-muted-foreground"
                        >
                          Consumer Key
                        </label>
                        <div className="flex items-center gap-2">
                          <span
                            id="view-edit-value-consumer-key"
                            className="text-sm"
                          >
                            {getSelectedApp(apiKeysQuery.data).consumerKey}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon-lg"
                            onClick={() => {
                              navigator.clipboard.writeText(
                                getSelectedApp(apiKeysQuery.data).consumerKey,
                              );
                            }}
                          >
                            <Copy />
                          </Button>
                        </div>
                      </div>
                      <div className="ml-3.5 flex h-10 flex-col">
                        <label
                          htmlFor="view-edit-value-consumer-secret"
                          className="text-xs text-muted-foreground"
                        >
                          Consumer Secret
                        </label>
                        <div className="flex items-center gap-2">
                          <span
                            id="view-edit-value-consumer-secret"
                            className="text-sm"
                          >
                            {getSelectedApp(apiKeysQuery.data).consumerSecret}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon-lg"
                            className=""
                            onClick={() => {
                              navigator.clipboard.writeText(
                                getSelectedApp(apiKeysQuery.data)
                                  .consumerSecret,
                              );
                            }}
                          >
                            <Copy />
                          </Button>
                        </div>
                      </div>
                      <div className="ml-3.5 flex h-10 flex-col">
                        <label
                          htmlFor="view-edit-value-expiry"
                          className="text-xs text-muted-foreground"
                        >
                          Expiry
                        </label>
                        <span id="view-edit-value-expiry" className="text-sm">
                          {getSelectedApp(apiKeysQuery.data).expireDate == -1
                            ? "Never"
                            : getSelectedApp(apiKeysQuery.data).expireDate}
                        </span>
                      </div>
                      <div className="ml-3.5 flex h-10 flex-col">
                        <label
                          htmlFor="view-edit-value-created"
                          className="text-xs text-muted-foreground"
                        >
                          Created
                        </label>
                        <span id="view-edit-value-created" className="text-sm">
                          {dayjs(getSelectedApp(apiKeysQuery.data).createdDate)
                            .tz("Australia/Sydney")
                            .format("D MMM, YYYY hh:mm:ss A z")}
                        </span>
                      </div>
                      <Field>
                        <FieldLabel htmlFor="notes">Note</FieldLabel>
                        <Textarea
                          id="notes"
                          name="notes"
                          value={notes}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={
                            !isEditing || apiKeyUpdateMutation.isPending
                          }
                          aria-invalid={touched.notes && Boolean(errors.notes)}
                          className="field-sizing-fixed min-h-20"
                        />
                        {touched.notes && errors.notes && (
                          <FieldError>{errors.notes}</FieldError>
                        )}
                      </Field>
                      <div
                        className={`flex flex-col ${isEditing ? "" : "mt-2"}`}
                      >
                        <label className="text-base font-medium">
                          API Products
                        </label>
                        <div className="grid grid-cols-1 pl-3.5 md:grid-cols-2">
                          {apiProductsQuery.data
                            .sort((a: any, b: any) =>
                              a.displayName.toUpperCase() >
                              b.displayName.toUpperCase()
                                ? 1
                                : -1,
                            )
                            .map((apigeeProduct: ApiProduct) => {
                              const isDisabled =
                                !isEditing ||
                                isDisabledPremiumData(
                                  apigeeProduct.premium &&
                                    apigeeProduct.premium == "full",
                                );
                              return (
                                <div
                                  key={apigeeProduct.name}
                                  className="flex items-center gap-2 py-1"
                                >
                                  <Checkbox
                                    id={`product-${apigeeProduct.name}`}
                                    checked={products.includes(
                                      apigeeProduct.name,
                                    )}
                                    onCheckedChange={(checked) => {
                                      const next = checked
                                        ? [...products, apigeeProduct.name]
                                        : products.filter(
                                            (p) => p !== apigeeProduct.name,
                                          );
                                      setFieldValue("products", next);
                                    }}
                                    disabled={isDisabled}
                                  />
                                  <Label
                                    htmlFor={`product-${apigeeProduct.name}`}
                                    className="cursor-pointer"
                                  >
                                    {apigeeProduct.displayName}
                                  </Label>
                                  {apigeeProduct.premium &&
                                    apigeeProduct.premium == "full" && (
                                      <TooltipWrapper
                                        style={{
                                          width: "24px",
                                          height: "24px",
                                        }}
                                        title={
                                          isDisabledPremiumData(
                                            (apigeeProduct.premium &&
                                              apigeeProduct.premium ==
                                                "full") ||
                                              apigeeProduct.premium ==
                                                "partial",
                                          )
                                            ? permissionDeniedMessageString(
                                                premiumPermissionKeys,
                                              )
                                            : "Premium Data"
                                        }
                                      >
                                        <Gem color="#1876d3" />
                                      </TooltipWrapper>
                                    )}
                                  {apigeeProduct.premium &&
                                    apigeeProduct.premium == "partial" && (
                                      <TooltipWrapper
                                        style={{
                                          width: "24px",
                                          height: "24px",
                                        }}
                                        title={
                                          isDisabledPremiumData(
                                            apigeeProduct.premium &&
                                              apigeeProduct.premium ==
                                                "partial",
                                          )
                                            ? permissionDeniedMessageString(
                                                premiumPermissionKeys,
                                                {
                                                  upgrade_required:
                                                    "Some functionality is limited to Team or higher",
                                                },
                                              )
                                            : "Premium Data"
                                        }
                                      >
                                        <Info />
                                      </TooltipWrapper>
                                    )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Info className="size-4" />
                          <span className="text-xs text-muted-foreground">
                            Products have some functionality that is only
                            accessible when you have a Team Plan or higher
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Gem className="size-4" color="#1876d3" />
                          <span className="text-xs text-muted-foreground">
                            Products are only accessible when you have a Team
                            Plan or higher
                          </span>
                        </div>
                      </div>
                    </FieldSet>
                  </CardContent>
                  <CardFooter className="justify-end">
                    {!isEditing && (
                      <div className="flex gap-2">
                        <PermissionTooltipButton
                          permissionKeys={[PermissionsKeys.appsUpdate]}
                          onClick={() => setIsEditing(true)}
                        >
                          Edit
                        </PermissionTooltipButton>
                        <PermissionTooltipButton
                          permissionKeys={[PermissionsKeys.appsDelete]}
                          variant="destructive"
                          onClick={() => {
                            apiKeyDeleteMutation.reset();
                            setDeleteModalIsOpen(true);
                          }}
                        >
                          Delete
                        </PermissionTooltipButton>
                      </div>
                    )}
                    {isEditing && (
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          disabled={apiKeyUpdateMutation.isPending}
                          onClick={() => {
                            resetForm();
                            apiKeyUpdateMutation.reset();
                            setIsEditing(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={
                            !isValid || !dirty || apiKeyUpdateMutation.isPending
                          }
                        >
                          {apiKeyUpdateMutation.isPending && <Spinner />}
                          Save
                        </Button>
                      </div>
                    )}
                  </CardFooter>
                  <ConfirmationModal
                    isLoading={apiKeyDeleteMutation.isPending}
                    isError={apiKeyDeleteMutation.isError}
                    open={deleteModalIsOpen}
                    header={`Delete this API key?`}
                    body={`Deleting "${selectedKey?.displayName}" API Key will permanently remove it from your account. This action cannot be undone.`}
                    leftButtonText={"CANCEL"}
                    leftButtonColor="primary"
                    rightButtonText={"DELETE API KEY"}
                    rightButtonColor="error"
                    handleLeftButton={() => setDeleteModalIsOpen(false)}
                    handleRightButton={() => apiKeyDeleteMutation.mutate()}
                    handleClose={() => setDeleteModalIsOpen(false)}
                  />
                </Form>
              );
            }}
          </Formik>
        )}
      </Card>
    </div>
  );
};
